import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AVAILABLE_VARIABLES } from '@/shared/lib/availableVariables';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Minus, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2,
  Type as TypeIcon, Undo, Redo, Pilcrow, Braces,
} from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  customVars?: Array<{ key: string; label: string }>;
}

interface ToolbarBtnProps {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}

function ToolbarBtn({ active, onClick, title, children, disabled }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`p-2 rounded-md transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-foreground hover:bg-muted'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function FontSizeSelect({ editor }: { editor: Editor }) {
  const sizes: Array<{ label: string; level?: 1 | 2 | 3; paragraph?: boolean }> = [
    { label: 'Normal', paragraph: true },
    { label: 'Titre 1', level: 1 },
    { label: 'Titre 2', level: 2 },
    { label: 'Titre 3', level: 3 },
  ];

  const current = editor.isActive('heading', { level: 1 })
    ? 'h1'
    : editor.isActive('heading', { level: 2 })
    ? 'h2'
    : editor.isActive('heading', { level: 3 })
    ? 'h3'
    : 'p';

  return (
    <select
      value={current}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => {
        const v = e.target.value;
        if (v === 'p') editor.chain().focus().setParagraph().run();
        else editor.chain().focus().toggleHeading({ level: Number(v.slice(1)) as 1 | 2 | 3 }).run();
      }}
      className="px-2 py-1 bg-background border border-border rounded-md text-sm focus:border-primary focus:outline-none cursor-pointer"
      title="Taille du texte"
    >
      {sizes.map((s) => (
        <option key={s.label} value={s.paragraph ? 'p' : `h${s.level}`}>
          {s.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Insert a variable token at the current cursor position. Pads with spaces so
 * adjacent words never collide (e.g. `Nom{first_name}` → `Nom {first_name}`).
 */
function VariableMenu({ editor, customVars }: { editor: Editor; customVars?: Array<{ key: string; label: string }> }) {
  const [open, setOpen] = useState(false);

  const insertToken = (token: string) => {
    const { state } = editor;
    const { from, to } = state.selection;
    const docText = state.doc.textBetween(0, state.doc.content.size, '\n', '\n');
    const before = docText.slice(0, from);
    const after = docText.slice(to);
    const needsLead = before.length > 0 && !/\s$/.test(before);
    const needsTrail = after.length > 0 && !/^\s/.test(after);
    const payload = `${needsLead ? ' ' : ''}${token}${needsTrail ? ' ' : ''}`;
    editor.chain().focus().insertContent(payload).run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          title="Insérer un champ dynamique"
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-foreground hover:bg-muted transition-colors"
        >
          <Braces size={16} />
          <span className="hidden sm:inline">Champs dynamiques</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {AVAILABLE_VARIABLES.map((group) => (
            <div key={group.category} className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2">
                {group.category}
              </p>
              {group.variables.map((v) => (
                <button
                  key={v.token}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertToken(v.token)}
                  className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted transition-colors group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{v.label}</span>
                    <code className="text-[11px] text-muted-foreground group-hover:text-foreground">
                      {v.token}
                    </code>
                  </div>
                  {v.description && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">{v.description}</p>
                  )}
                </button>
              ))}
            </div>
          ))}
          {customVars && customVars.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-2">
                Champs du formulaire
              </p>
              {customVars.map((v) => {
                const token = `{{${v.key}}}`;
                return (
                  <button
                    key={v.key}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertToken(token)}
                    className="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{v.label || v.key}</span>
                      <code className="text-[11px] text-muted-foreground group-hover:text-foreground">
                        {token}
                      </code>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 120, customVars }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose-editor focus:outline-none w-full',
        'data-placeholder': placeholder || '',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat empty editor as empty string
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g. language switch)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current && incoming !== (current === '<p></p>' ? '' : current)) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border-2 border-border rounded-xl bg-background overflow-hidden focus-within:border-primary transition-colors">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted/30">
        <FontSizeSelect editor={editor} />

        <div className="w-px h-6 bg-border mx-1" />

        <ToolbarBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Gras (Ctrl+B)"
        >
          <Bold size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italique (Ctrl+I)"
        >
          <Italic size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Souligné (Ctrl+U)"
        >
          <UnderlineIcon size={16} />
        </ToolbarBtn>

        <div className="w-px h-6 bg-border mx-1" />

        <ToolbarBtn
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Puces"
        >
          <List size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Liste numérotée"
        >
          <ListOrdered size={16} />
        </ToolbarBtn>

        <div className="w-px h-6 bg-border mx-1" />

        <ToolbarBtn
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="Aligner à gauche"
        >
          <AlignLeft size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Centrer"
        >
          <AlignCenter size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="Aligner à droite"
        >
          <AlignRight size={16} />
        </ToolbarBtn>

        <div className="w-px h-6 bg-border mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Ligne horizontale (tiret)"
        >
          <Minus size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setHardBreak().run()}
          title="Saut de ligne (Maj+Entrée)"
        >
          <Pilcrow size={16} />
        </ToolbarBtn>

        <div className="w-px h-6 bg-border mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Annuler (Ctrl+Z)"
        >
          <Undo size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Rétablir (Ctrl+Y)"
        >
          <Redo size={16} />
        </ToolbarBtn>

        <div className="w-px h-6 bg-border mx-1" />

        <VariableMenu editor={editor} customVars={customVars} />
      </div>

      {/* Editor area */}
      <div
        className="px-4 py-3 cursor-text"
        style={{ minHeight }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
